import React, { memo, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import { getParkingTagIcon, InfoTagType } from '@/constants/infoTagsAssets'
import Spinner from '@/components/common/SpinnerPage'
import { usePandfTowns } from '@/service/apiHooks/parkingQueries'
import { useNavigationTracking } from '@/hooks/useNavigationTracking'
import { useMarket } from '@/utils/multi-markets/context'
import { useIsMobile } from '@/utils/useMobile'

import {
  HintsList,
  HintsListItem,
  HintsListItemLink,
  Wrapper,
  CategoryWrapper,
  IconWrapper,
  ShowAll
} from './SearchHintsMainPage.styled'
import HintAirportsBottomSheet from './HintAirportsBottomSheet'

const SearchHintsMainPage = () => {
  const router = useRouter()
  const { locale, market } = useMarket()
  const { t } = useTranslation(['home', 'common'])
  const { isMobile } = useIsMobile()
  const { trackClick } = useNavigationTracking()
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
  const region = market
  const { data: airportsData } = usePandfTowns(locale, region)
  const airports = airportsData?.airports || []

  if (!airports.length) {
    return (
      <Spinner aria-label={t('home.categories.loading' as keyof typeof t)} />
    )
  }

  const handleAirportClick = (airport: any) => {
    trackClick(`main_page_search_hints_airport_${airport.code}`)
    router.push({
      pathname: '/parking-search',
      query: {
        radius: 100000,
        service: 'PARK_AND_FLY',
        lat: airport.lat,
        lon: airport.lon,
        locationLabel: `${airport.code} - ${airport.cityName}`,
        cityUrlSlug: airport.cityUrlSlug
      }
    })
  }

  const getAirportHref = (airport: any) => {
    const params = new URLSearchParams({
      radius: '100000',
      service: 'PARK_AND_FLY',
      lat: airport.lat.toString(),
      lon: airport.lon.toString(),
      locationLabel: `${airport.code} - ${airport.cityName}`,
      cityUrlSlug: airport.cityUrlSlug
    })
    return `/parking-search?${params.toString()}`
  }

  const openBottomSheet = () => {
    setIsBottomSheetOpen(true)
  }

  const closeBottomSheet = () => {
    setIsBottomSheetOpen(false)
  }

  return (
    <Wrapper>
      <nav aria-label={t('home.categories.navigation' as keyof typeof t)}>
        {market !== 'bh' && (
          <CategoryWrapper>
            {isMobile ? (
              <>
                <IconWrapper
                  onClick={openBottomSheet}
                  style={{ cursor: 'pointer' }}
                >
                  <img
                    src={getParkingTagIcon(InfoTagType.PLANE_ICON)}
                    alt="Plane icon"
                    width={10}
                    height={10}
                    style={{
                      width: '10px',
                      height: '10px'
                    }}
                  />
                </IconWrapper>
                <ShowAll
                  href={`/parking-search?service=PARK_AND_FLY`}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault()
                    openBottomSheet()
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {t('common:navigation.showAllAirports')}
                </ShowAll>
              </>
            ) : (
              <>
                <IconWrapper>
                  <img
                    src={getParkingTagIcon(InfoTagType.PLANE_ICON)}
                    alt="Plane icon"
                    width={16}
                    height={16}
                    style={{
                      width: '16px',
                      height: '16px',
                      position: 'absolute',
                      top: '7px',
                      left: '7px'
                    }}
                  />
                </IconWrapper>
                <HintsList>
                  {airports.map((airport, idx) => (
                    <HintsListItem
                      key={`airport-${airport.code}-${idx}`}
                      data-test-id={`airport-${airport.code}`}
                    >
                      <HintsListItemLink
                        href={getAirportHref(airport)}
                        onClick={(e: React.MouseEvent) => {
                          e.preventDefault()
                          handleAirportClick(airport)
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={t(
                          'home.categories.searchAtAirport' as keyof typeof t,
                          {
                            code: airport.code,
                            city: airport.cityName
                          }
                        )}
                        onKeyPress={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleAirportClick(airport)
                          }
                        }}
                      >
                        {`${airport.code} - ${airport.cityName}`}
                      </HintsListItemLink>
                    </HintsListItem>
                  ))}
                </HintsList>
              </>
            )}
          </CategoryWrapper>
        )}
      </nav>

      <HintAirportsBottomSheet
        airports={airports}
        onCancel={closeBottomSheet}
        isOpen={isBottomSheetOpen}
      />
    </Wrapper>
  )
}

export default memo(SearchHintsMainPage)
